import {
  Alert,
  Box,
  Chip,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import type {
  ContactConflictLead,
  ContactConflictPreview,
} from "../../dtos/contact-conflicts";
import { contactConflictLeadPath } from "../../utils/contact-conflicts";

function LeadDetails({ lead }: { lead: ContactConflictLead }) {
  const path = contactConflictLeadPath(lead.nanoId);
  return (
    <Stack spacing={0.5} sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
      <Typography fontWeight={600}>{lead.name}</Typography>
      <Typography variant="body2">
        {lead.leadType} · {lead.agentName || "Unassigned"}
      </Typography>
      <Box>
        <Chip
          size="small"
          label={lead.active ? "Active" : "Inactive"}
          color={lead.active ? "success" : "default"}
        />
      </Box>
      {path && (
        <Link
          href={path}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
        >
          Open lead {lead.nanoId}
        </Link>
      )}
    </Stack>
  );
}

export function ContactConflictDetails({
  preview,
  selections,
  onSelect,
  disabled,
}: {
  preview: ContactConflictPreview;
  selections: Record<string, string>;
  onSelect: (key: string, journeyUId: string) => void;
  disabled: boolean;
}) {
  return (
    <Stack spacing={2}>
      <Typography variant="body2">
        Choose the lead to keep for each duplicate pair. Its current agent stays
        assigned. All affected leads are shown below.
      </Typography>
      {preview.groups.map((group, index) => {
        const labelId = `contact-conflict-group-${index}`;
        const winner = group.leads.find(
          (lead) =>
            lead.journeyUId && lead.journeyUId === selections[group.key],
        );
        const losers = winner
          ? group.leads.filter((lead) => lead.uid !== winner.uid)
          : [];
        return (
          <Stack key={group.key} spacing={1}>
            <Typography id={labelId} fontWeight={600}>
              {[...new Set(group.leads.map((lead) => lead.leadType))].join(
                " / ",
              )}
              {group.requiresChoice
                ? " — choose which lead to keep"
                : " — kept separately"}
            </Typography>
            {group.requiresChoice ? (
              <RadioGroup
                aria-labelledby={labelId}
                value={selections[group.key] || ""}
                onChange={(_, value) => onSelect(group.key, value)}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1,
                }}
              >
                {group.leads.map((lead) => (
                  <Stack
                    key={lead.uid}
                    spacing={1}
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor:
                        winner?.uid === lead.uid ? "primary.main" : "divider",
                      borderRadius: 2,
                      minWidth: 0,
                    }}
                  >
                    <FormControlLabel
                      value={lead.journeyUId || ""}
                      disabled={disabled || !lead.journeyUId}
                      control={<Radio size="small" />}
                      label={`Keep ${lead.name}`}
                      sx={{
                        alignItems: "flex-start",
                        m: 0,
                        overflowWrap: "anywhere",
                        "& .MuiRadio-root": { pt: 0, pl: 0 },
                      }}
                    />
                    <LeadDetails lead={lead} />
                    {!lead.journeyUId && (
                      <Typography color="error" variant="body2">
                        No current journey available.
                      </Typography>
                    )}
                  </Stack>
                ))}
              </RadioGroup>
            ) : (
              group.leads.map((lead) => (
                <Box
                  key={lead.uid}
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <LeadDetails lead={lead} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Lead and assignment stay unchanged.
                  </Typography>
                </Box>
              ))
            )}
            {winner && (
              <Alert severity="info">
                Keep {winner.name} with{" "}
                {winner.agentName || "no assigned agent"}.
                {losers.length > 0 &&
                  ` Archive ${losers.map((lead) => lead.name).join(", ")} and clear their assignments.`}
              </Alert>
            )}
          </Stack>
        );
      })}
      {preview.requests.length > 0 && (
        <Stack spacing={1}>
          <Typography fontWeight={600}>Pending contact information</Typography>
          {preview.requests.map((request, index) => (
            <Box
              key={`${request.receivedAt}-${index}`}
              sx={{
                p: 1.5,
                bgcolor: "action.hover",
                borderRadius: 1,
                overflowWrap: "anywhere",
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {request.source === "cms"
                  ? "CMS request"
                  : "Phone added by an agent"}
              </Typography>
              {request.telephone && (
                <Typography variant="body2">
                  Phone: {request.telephone}
                </Typography>
              )}
              {request.username && (
                <Typography variant="body2">
                  WhatsApp: @{request.username.replace(/^@/, "")}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {new Date(request.receivedAt).toLocaleString()}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
      <Alert severity="warning">
        Contacts and history are combined. Pending follow-ups from active leads
        move to the surviving lead; inactive follow-ups are not resumed. Sonder
        tasks stay unchanged.
        {preview.requests.some((request) => request.source === "cms") &&
          " Pending CMS requests continue after resolution."}
      </Alert>
    </Stack>
  );
}
